import { Component, computed, effect, Injector, OnDestroy, OnInit, Signal, signal, ViewEncapsulation, WritableSignal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { RouterOutlet } from '@angular/router';
import { SettingsState } from '@app/features/admin/settings/state/settings.state';
import { TranslocoModule } from '@ngneat/transloco';
import {
  SealFullscreenComponent,
  SealLoadingBarComponent,
  SealMediaWatcherService,
  SealNavigationService,
  SealVerticalNavigationComponent
} from '@shipsfocus/seal';
import { NavigationState } from 'app/core/navigation/navigation.state';
import { Navigation } from 'app/core/navigation/navigation.types';
import { ProfileComponent } from 'app/layout/common/profile/profile.component';
import { environment } from 'environments/environment';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'classic-layout',
  templateUrl: './classic.component.html',
  encapsulation: ViewEncapsulation.None,
  imports: [
    RouterOutlet,
    TranslocoModule,
    MatButtonModule,
    MatIconModule,
    ProfileComponent,
    SealFullscreenComponent,
    SealLoadingBarComponent,
    SealVerticalNavigationComponent
  ]
})
export class ClassicLayoutComponent implements OnInit, OnDestroy {
  isScreenSmall: WritableSignal<boolean> = signal(void 0);
  navigation: Signal<Navigation> = computed(() => this._navigationState.navigation());
  protected readonly environment: typeof environment = environment;
  private _unsubscribeAll: Subject<any> = new Subject<any>();

  constructor(
    private _injector: Injector,
    private _sealMediaWatcherService: SealMediaWatcherService,
    private _sealNavigationService: SealNavigationService,
    private _settingsState: SettingsState,
    private _navigationState: NavigationState
  ) {}

  ngOnInit(): void {
    effect(
      () => {
        if (this.navigation() && this._settingsState.userPermissions()) {
          const userPermission = this._settingsState.userPermissions();

          this.navigation().default.forEach((nav) => {
            nav.children?.forEach((child) => {
              const page = child.id.split('.')[1];
              if (!(userPermission[page] && !userPermission[page][`view_${page}`])) {
                child.hidden = () => !(userPermission[page] && !userPermission[page][`view_${page}`]);
              } else {
                child.hidden = child.hidden || (() => false);
              }
            });

            nav.hidden = () => nav.children?.every((child) => child.hidden(child));

            const divider = this._sealNavigationService.getItem(`${nav.id}.divider`, this.navigation().default);

            if (divider) {
              divider.hidden = () => nav.children?.every((child) => child.hidden(child));
            }
          });
        }
      },
      { injector: this._injector }
    );

    this._sealMediaWatcherService.onMediaChange$.pipe(takeUntil(this._unsubscribeAll)).subscribe(({ matchingAliases }: { matchingAliases: string[] }) => {
      this.isScreenSmall.set(!matchingAliases.includes('md'));
    });
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  toggleNavigation(name: string): void {
    const navigation = this._sealNavigationService.getComponent<SealVerticalNavigationComponent>(name);

    if (navigation) {
      navigation.toggle();
    }
  }
}
